/// <amd-module name="VSSUI/Components/TeachingPanel/TeachingPanel" />
import * as React from "react";
import { Button } from "VSSUI/Button";
import { Callout, ContentJustification, ContentLocation, ContentOrientation } from "VSSUI/Callout";
import { FocusZone } from "VSSUI/FocusZone";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";

import * as Resources from "VSSUI/Resources.TeachingPanel";
import { ITeachingPanelProps, ITeachingSlideProps } from "./TeachingPanel.Props";

interface ITeachingPanelState {
    slideIndex: number;
    shouldShow: boolean;
}

export class TeachingPanel extends React.Component<ITeachingPanelProps, ITeachingPanelState> {
    private nextButton = React.createRef<Button>();

    constructor(props: ITeachingPanelProps) {
        super(props);

        this.state = {
            slideIndex: 0,
            shouldShow: true
        };
    }

    public render() {
        return this.state.shouldShow ? this._renderPanel() : null;
    }

    private _onPanelClose = (): void => {
        this.setState({ shouldShow: false });
        this.props.onDismiss && this.props.onDismiss(this.state.slideIndex);
    };

    private _onNextPressed = (): void => {
        if (this.state.slideIndex + 1 === this.props.slides.length) {
            this._onPanelClose();
        } else {
            this.setState({ slideIndex: this.state.slideIndex + 1 });
        }
    };

    private _onBackPressed = (): void => {
        if (this.state.slideIndex > 0) {
            // If we are on the second slide before moveing to the first slide, set
            // focus to the next button, otherwise focus is lost.
            if (this.state.slideIndex === 1 && this.nextButton.current) {
                this.nextButton.current.focus();
            }

            this.setState({ slideIndex: this.state.slideIndex - 1 });
        }
    };

    private _renderPanel = (): JSX.Element | null => {
        const currentSlide: ITeachingSlideProps = this.props.slides[this.state.slideIndex];
        const firstSlide: boolean = this.state.slideIndex === 0;
        const lastSlide: boolean = this.state.slideIndex + 1 === this.props.slides.length;
        if (!currentSlide) {
            return null;
        }

        return (
            <Callout
                className="bolt-teaching-panel-layer"
                contentClassName={(this.props.className ?? "bolt-teaching-panel") + " depth-16"} 
                contentJustification={ContentJustification.Center}
                contentLocation={ContentLocation.Center}
                contentOrientation={ContentOrientation.Column}
                contentShadow={true}
                escDismiss={true}
                lightDismiss={true}
                modal={true}
                onDismiss={this._onPanelClose}
            >
                <FocusZone circularNavigation={true} defaultActiveElement=".bolt-teaching-panel-next" focusOnMount={true} handleTabKey={true}>
                    <div className="bolt-teaching-panel-container flex-grow flex-column scroll-hidden">
                        <div className="bolt-teaching-panel-visual flex-row flex-noshrink">
                            <img className="bolt-teaching-panel-image flex-row" src={currentSlide.slideImage} alt={currentSlide.imageAltText} />
                            <Button
                                subtle={true}
                                className="bolt-teaching-pane-close-button"
                                ariaLabel={Resources.Close}
                                iconProps={{ iconName: "Cancel" }}
                                onClick={this._onPanelClose}
                            />
                        </div>
                        <div className="bolt-teaching-panel-description flex-column flex-grow padding-16 rhythm-vertical-16 scroll-hidden">
                            <Tooltip overflowOnly={true}>
                                <span className="bolt-teaching-panel-header title-m text-ellipsis flex-noshrink">{currentSlide.title}</span>
                            </Tooltip>
                            <span
                                className="bolt-teaching-panel-text flex-grow body-l v-scroll-auto custom-scrollbar"
                                dangerouslySetInnerHTML={{ __html: currentSlide.description }}
                            />
                            <div className="bolt-teaching-panel-buttons flex-row relative">
                                <Button
                                    text={Resources.Back}
                                    onClick={this._onBackPressed}
                                    className={css("bolt-teaching-panel-back", firstSlide && "first-slide")}
                                    disabled={this.state.slideIndex <= 0}
                                />
                                <Button
                                    ref={this.nextButton}
                                    primary={true}
                                    text={lastSlide ? Resources.Done : Resources.Next}
                                    onClick={this._onNextPressed}
                                    className="bolt-teaching-panel-next"
                                />
                                <div className="bolt-teaching-panel-progress flex-row flex-grow flex-center justify-center rhythm-horizontal-8 absolute-fill">
                                    {this.props.slides.map((item, index) => {
                                        return (
                                            <div
                                                key={"progress-" + index}
                                                className={css("bolt-teaching-panel-progress-circle", this.state.slideIndex === index && "current")}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </FocusZone>
            </Callout>
        );
    };
}
